import { useState, useCallback, useRef, useEffect } from "react";
import { GoogleMap, Polygon, DrawingManager, Marker } from "@react-google-maps/api";
import { AGRO_API_KEY } from "../../utils/Helpers";
import { CONSTANTS } from "../../utils/Constants";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { COLORS } from "../../utils/Colors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";

const mapContainerStyle = {
    width: "100%",
    height: "100%",
};

type NDVIMapProps = {
    isLoaded: boolean;
    lat: number;
    lng: number;
    customerAlias: string;
    customerId: string;
    onSelectPolygonId: (polyId: string | null) => void;
    setShowAnalyze: (showAnalyze: boolean) => void
};

const NDVIMap = ({ isLoaded, lat, lng, customerAlias, customerId, onSelectPolygonId, setShowAnalyze }: NDVIMapProps) => {
    const [polygonCoords, setPolygonCoords] = useState<{ lat: number; lng: number }[]>([]);
    const [ndviImage, setNdviImage] = useState<string | null>(null);
    const [toggleDrawingMode, setToggleDrawingMode] = useState<boolean>(false);
    const [drawingMode, setDrawingMode] = useState<google.maps.drawing.OverlayType | null>(google.maps.drawing.OverlayType.POLYGON);
    const drawingManagerRef = useRef<DrawingManager | null>(null);
    const polygonRef = useRef<google.maps.Polygon | null>(null);

    useEffect(() => {
        if (isLoaded) {
            setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
        }
    }, [isLoaded]);

    const onPolygonComplete = useCallback((polygon: google.maps.Polygon) => {
        polygonRef.current = polygon;
        const path = polygon.getPath();
        const coordinates = [];
        for (let i = 0; i < path.getLength(); i++) {
            const latLng = path.getAt(i);
            coordinates.push({ lat: latLng.lat(), lng: latLng.lng() });
        }
        setPolygonCoords(coordinates);
        fetchNDVI(coordinates);
        setToggleDrawingMode(false);
        setDrawingMode(null)
    }, []);

    // Fetch NDVI Image from Agromonitoring
    const fetchNDVI = async (coords: { lat: number; lng: number }[]) => {
        const startDate = Math.floor(new Date("2024-01-01").getTime() / 1000);
        const endDate = Math.floor(new Date().getTime() / 1000);

        if (coords.length > 0) {
            coords.push(coords[0]);
        }
        const polygonGeoJson = {
            name: customerAlias,
            geo_json: {
                type: "Feature",
                properties: {
                    "user_id": customerId,
                },
                geometry: {
                    type: "Polygon",
                    coordinates: [coords.map(({ lat, lng }) => [lng, lat])],
                },
                reference_id: customerId
            }
        };

        try {
            const polyResponse = await fetch(`https://api.agromonitoring.com/agro/1.0/polygons?appid=${AGRO_API_KEY}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(polygonGeoJson),
            });

            const polyData = await polyResponse.json();
            console.log(polyData)
            if (!polyData.id) throw new Error("Failed to create polygon", polyData);
            onSelectPolygonId(polyData.id)
            const ndviResponse = await fetch(
                `https://api.agromonitoring.com/agro/1.0/image/search?start=${startDate}&end=${endDate}&polyid=${polyData.id}&appid=${AGRO_API_KEY}`
            );

            const ndviData = await ndviResponse.json();
            if (ndviData.length > 0) {
                setNdviImage(ndviData[0].ndvi || ndviData[0].truecolor);
            }
        } catch (error) {
            console.error("Error fetching NDVI data:", error);
        }
    };

    const { height } = useWindowDimensions();

    if (!isLoaded) return <p>Loading map...</p>;

    return (
        <div
            className="flex absolute z-10 flex-col bg-blue-50 w-96 h-full gap-2 rounded-xl items-center"
            style={{
                boxShadow: "rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px",
                height: height * 0.5,
                scrollbarWidth: "thin",
            }}>
            <div
                className="flex h-14 justify-center text-center items-center text-white rounded-b-2xl rounded-t-xl w-full font-semibold text-2xl"
                style={{ background: COLORS.Brand }}>
                <div className="flex w-1/2 justify-end text-end flex-1 text-2xl font-semibold text-white">
                    {CONSTANTS.drawPolygon}
                </div>
                <div className="flex w-1/2 justify-end items-center text-2xl hover:cursor-pointer" >
                    <FontAwesomeIcon onClick={() => { setShowAnalyze(false) }} className="w-5 h-5 pr-2" color="white" icon={faClose} />

                </div>
            </div>

            <div className="relative w-full h-10 flex justify-around items-center">
                <button
                    onClick={() => setToggleDrawingMode(true)}
                    disabled={!!polygonRef.current}
                    className={`px-6 py-1 rounded-md shadow-sm mx-2 ${polygonRef.current ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 text-white"
                        }`}>
                    Start
                </button>
                <button
                    onClick={() => {
                        setPolygonCoords([])
                        setToggleDrawingMode(false)
                        setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
                        setNdviImage(null)
                        onSelectPolygonId(null)
                        if (polygonRef.current) {
                            polygonRef.current.setMap(null);
                            polygonRef.current = null;
                        }
                    }}
                    className="px-6 py-1 bg-red-600 text-white rounded-md shadow-sm mx-2">
                    Clear
                </button>
            </div>

            <div
                className="flex flex-col flex-1 items-center justify-center w-11/12  top-16 bg-white rounded-xl drop-shadow-sm"
                style={{ height: "10%" }}>
                {isLoaded && (
                    <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={{
                            lat: lat,
                            lng: lng,
                        }}

                        zoom={12}
                        options={{
                            fullscreenControlOptions: {
                                position: window.google.maps.ControlPosition.TOP_LEFT,
                            },
                            mapTypeControl: true,
                            mapTypeControlOptions: {
                                style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
                                position: window.google.maps.ControlPosition.TOP_RIGHT,
                            },
                            mapTypeId: window.google.maps.MapTypeId.HYBRID,
                            minZoom: 3,
                        }}>

                        {toggleDrawingMode && (
                            <DrawingManager
                                ref={drawingManagerRef}
                                options={{
                                    drawingControl: false,
                                    drawingMode: polygonRef.current ? null : drawingMode,
                                    polygonOptions: {
                                        fillColor: "#00FF00",
                                        fillOpacity: 0.35,
                                        strokeWeight: 2,
                                        strokeColor: "#00FF00",
                                        clickable: true,
                                        editable: true,
                                        draggable: true,
                                    },
                                }}
                                onPolygonComplete={(polygon: google.maps.Polygon) => {
                                    if (!polygonRef.current) {
                                        polygonRef.current = polygon;
                                        setDrawingMode(null)
                                        setToggleDrawingMode(false)
                                        onPolygonComplete(polygon)
                                    } else {
                                        polygon.setMap(null);
                                    }
                                }}
                            />
                        )}

                        {polygonCoords.length > 0 && (
                            <Polygon paths={polygonCoords} options={{ fillColor: "#FF0000", fillOpacity: 0.35 }} />
                        )}

                        {ndviImage && (
                            <img
                                src={ndviImage}
                                alt="NDVI"
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    opacity: 0.6,
                                }}
                            />
                        )}
                        <Marker
                            position={{ lat, lng }}
                            icon={{
                                url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                            }}
                        />
                    </GoogleMap>
                )}
            </div>

        </div>
    );
};

export default NDVIMap;
